/**
 * The right-hand half: the illustration while the trainer is choosing, the client's own calendar
 * once they have, and the confirmation when the plan has landed.
 *
 * ── The swap changes INK, not geometry ──────────────────────────────────────────────────────
 * The panel is one box of one size in every state. The ambient illustration, the calendar and the
 * self-paced list all stand inside it, so the moment a start date is chosen nothing below the
 * dialog moves and nothing in the form jumps — which is the loading policy applied to a swap the
 * trainer causes rather than to a read.
 *
 * ── WHICH of the four is a decision, and it is not made here ────────────────────────────────
 * `useAssignFlow` answers with one `AssignPanel`. The alternative — four null tests that have to
 * agree with each other — is how a panel ends up drawing two of its states at once, or none.
 *
 * ── The loop stops when the dialog closes ───────────────────────────────────────────────────
 * `running` is the dialog's own open flag. Both illustrations park on their last frame when it goes
 * false, so nothing animates behind a closed popup and nothing animates for a viewer who asked for
 * stillness.
 */

import { useTranslation } from 'react-i18next';

import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';
import { CalendarWeekViz } from '~/shared/ui/planSystemViz';

import type { AssignFlow } from '../lib/useAssignFlow';
import type { ProjectionMove } from '../lib/useProjectionMove';

import { ProjectionCalendar } from './ProjectionCalendar';
import { SelfPacedList } from './SelfPacedList';
import styles from './AssignPlanDialog.module.css';

interface AssignProjectionProps {
  readonly flow: AssignFlow;
  readonly move: ProjectionMove;
  readonly reducedMotion: boolean;
  readonly running: boolean;
}

export function AssignProjection({ flow, move, reducedMotion, running }: AssignProjectionProps) {
  const { t } = useTranslation();
  const { panel } = flow;

  return (
    <>
      <div className={styles.liveHead}>
        <b className={styles.liveTitle}>{panel.title}</b>
        <span className={styles.liveSubtitle}>{panel.subtitle}</span>
        <span className={styles.liveSpacer} />
        {/* Offered only when it reveals something. A control that expands a plan already drawn in
          * full is a control that does nothing. */}
        {panel.expandable && (
          <button type="button" className={styles.liveToggle} onClick={flow.toggleExpanded}>
            {flow.expanded ? t('web:assign.live.firstWeeks') : t('web:assign.live.wholePlan')}
          </button>
        )}
      </div>

      {panel.body.kind === 'sequence' && (
        <SelfPacedList review={panel.body.review} reducedMotion={reducedMotion} running={running} />
      )}

      {panel.body.kind === 'ambient' && (
        <div className={styles.ambient}>
          <div className={styles.ambientViz}>
            <CalendarWeekViz reducedMotion={reducedMotion} running={running} foot={false} />
          </div>
          <p className={styles.ambientNote}>{panel.body.note}</p>
        </div>
      )}

      {panel.body.kind === 'empty' && <p className={styles.ambientNote}>{panel.body.message}</p>}

      {panel.body.kind === 'calendar' && (
        <ProjectionCalendar projection={panel.body.projection} move={move} flow={flow} />
      )}

      {/* The live region for the move. Always mounted; empty when there is nothing to say — a
        * region inserted together with its text is not reliably announced. */}
      <LiveRegionAnnouncer message={move.announcement} />
    </>
  );
}
